import React, { useContext, useEffect, useImperativeHandle, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { getEntities, postEntities, postEntitiesAsBlob } from 'app/api/generalApi';
import { ToastContext } from 'app/context/ToastContext';
import axios from 'axios';
import { downloadExcelFile } from 'app/api/apiUtil';
import { decode, encode } from 'app/shared/util/encode-utils';

class Props {
  ref?: any;
  initialSortField: any;
  initialSortDirection?: any;
  createUrl: string;
  serverUrl: string;
  labelForCreateEntity?: string;
  globalFilterFields: string[] | undefined;
  columns: any;
  editActionAllowed?: boolean;
  infoActionAllowed?: boolean;
  deleteActionAllowed?: boolean;
  exportUrl?: string;
  additionalActions?: any;
  onSelectionChange?: any;
  selectedItems?: any;
  additionalHeaderButtons?: any;
  externalLoading?: boolean | undefined;
  searchAllowed?: boolean | undefined;
  filters?: any;
  additionalHeaderFilters?: any[] | undefined;
  rowClassName?: any;
  title?: any;
  blockInitialUrlParameters?: boolean;
}

const LazyTable = ({
  ref,
  initialSortField,
  initialSortDirection,
  createUrl,
  serverUrl,
  labelForCreateEntity,
  globalFilterFields,
  columns,
  editActionAllowed,
  infoActionAllowed,
  exportUrl,
  deleteActionAllowed,
  additionalActions,
  onSelectionChange,
  selectedItems,
  additionalHeaderButtons,
  externalLoading,
  searchAllowed = true,
  filters,
  additionalHeaderFilters,
  rowClassName,
  title,
  blockInitialUrlParameters,
}: Props) => {
  const [searchParams] = useSearchParams();

  const initData = !blockInitialUrlParameters && searchParams.get('p') ? (decode(searchParams.get('p'), {}) as any) : null;

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [entities, setEntities] = useState(null);
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(null);
  const [selectedRepresentative, setSelectedRepresentative] = useState(null);
  const [filterValues, setFilterValues] = useState(initData?.filters || filters);
  const [filterValue, setFilterValue] = useState<string | null>(initData?.filter);
  const [deleteContactDialog, setDeleteContactDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lazyParams, setLazyParams] = useState({
    first: initData?.page ? initData?.page * 20 : 0,
    rows: 20,
    page: initData?.page ?? 0,
    sortField: initData?.sort ?? initialSortField,
    sortOrder: initData?.dirertion
      ? initData?.direction === 2
        ? SortOrder.DESC
        : SortOrder.ASC
      : initialSortDirection && initialSortDirection === 'desc'
        ? SortOrder.DESC
        : SortOrder.ASC,
    filter: '',
    filters: null,
  });

  useEffect(() => {
    loadData();
  }, [lazyParams, additionalHeaderFilters]);

  useEffect(() => {
    if (filterValue || filterValue === '' || filterValues) {
      const delayDebounceFn = setTimeout(() => {
        if (isInitialized) {
          setLazyParams({
            ...lazyParams,
            first: 0,
            page: 0,
            filter: filterValue,
          });
        } else {
          setLazyParams({
            ...lazyParams,
            filter: filterValue,
          });
        }
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [filterValue, filterValues]);

  useImperativeHandle(ref, () => ({
    getLazyParams: () => lazyParams,
    reloadData: loadData,
    getEncodedParams: getTempParameters,
  }));

  const serializeFilterValues = filtersToSerialize => {
    if (!filtersToSerialize) return null;

    const serialized = {};
    for (const key in filtersToSerialize) {
      if (Object.prototype.hasOwnProperty.call(filtersToSerialize, key)) {
        const filter = filtersToSerialize[key];
        if (filter && filter.value !== null && filter.value !== undefined) {
          // Check if value is an array (date range)
          if (Array.isArray(filter.value) && filter.value.length > 0 && filter.value[0] instanceof Date) {
            const dates = filter.value;
            const formatDate = (d: Date) => {
              if (!d) return 'null';
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            };
            serialized[key] = {
              ...filter,
              value: `${formatDate(dates[0])},${formatDate(dates[1])}`,
            };
          } else if (Array.isArray(filter.value)) {
            // Date range from DateTextInput: array of YYYY-MM-DD strings
            const [from, to] = filter.value;
            serialized[key] = {
              ...filter,
              value: `${from || 'null'},${to || 'null'}`,
            };
          } else if (typeof filter.value === 'boolean') {
            // Convert boolean to string for backend Filter.java
            serialized[key] = {
              ...filter,
              value: String(filter.value),
            };
          } else {
            serialized[key] = filter;
          }
        } else {
          serialized[key] = filter;
        }
      }
    }
    return serialized;
  };

  const loadData = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      ...(lazyParams.filter || additionalHeaderFilters
        ? {
            query: lazyParams.filter ?? null,
            additionalFilters: additionalHeaderFilters,
          }
        : {}),
    };

    const serializedFilters = serializeFilterValues(filterValues);
    const call = serializedFilters ? postEntities(serverUrl, requestData, serializedFilters) : getEntities(serverUrl, requestData);

    call
      .then(response => {
        setEntities(response.data.content);
        setTotalRecords(response.data.totalElements);
        setIsInitialized(true);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
        setIsInitialized(true);
      });
  };

  const onPage = event => {
    setLazyParams({ ...event, filter: filterValue });
  };

  const onSort = event => {
    setLazyParams({ ...event, page: lazyParams.page, filter: filterValue });
  };

  // Handle the creation of a new agent
  const handleCreate = () => {
    navigate(createUrl + '?p=' + getTempParameters()); // Navigate to the creation page
  };

  const handleExport = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      ...(lazyParams.filter || additionalHeaderFilters ? { query: lazyParams.filter, additionalFilters: additionalHeaderFilters } : {}),
    };

    postEntitiesAsBlob(exportUrl, requestData, filterValues)
      .then(response => {
        downloadExcelFile(response);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  function getTempParameters() {
    const p = {
      page: lazyParams.page,
      sort: lazyParams.sortField,
      direction: lazyParams.sortOrder === SortOrder.ASC ? 1 : 2,
      filters: filterValues,
      filter: filterValue,
    };

    return encode(p);
  }

  // Handle the editing of an existing agent
  const handleEdit = rowData => {
    navigate(`${createUrl}/${rowData.id}?p=${getTempParameters()}`); // Navigate to the edit page
  };

  const handleInfo = rowData => {
    navigate(`${createUrl}/${rowData.id}?info&p=${getTempParameters()}`);
  };

  // const onSelectionChange = event => {
  //   const value = event.value;
  //   // setSelectedContacts(value);
  //   // setSelectAll(value.length === totalRecords);
  // };

  const confirmDeleteContact = contactToDelete => {
    // setProduct(product);
    setEntity(contactToDelete);
    setDeleteContactDialog(true);
  };

  const hideDeleteContactDialog = () => {
    setDeleteContactDialog(false);
  };

  const deleteContact = () => {
    setLoading(true);

    axios
      .delete(`${serverUrl}/${entity.id}?cacheBuster=${new Date().getTime()}`)
      .then(r => {
        setDeleteContactDialog(false);
        setLazyParams({ ...lazyParams, page: 0 });
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          {infoActionAllowed && (
            <Button icon="pi pi-info" tooltip="Info" rounded outlined className="mr-2" onClick={() => handleInfo(rowData)} />
          )}
          {editActionAllowed && (
            <Button icon="pi pi-pencil" tooltip="Editovať" rounded outlined className="mr-2" onClick={() => handleEdit(rowData)} />
          )}
          {deleteActionAllowed && (
            <Button icon="pi pi-trash" tooltip="Vymazať" rounded outlined severity="danger" onClick={() => confirmDeleteContact(rowData)} />
          )}

          {additionalActions?.map((col, index) => (
            <Button
              key={index}
              icon={col.icon}
              hidden={col.hidden ? col.hidden(rowData) : false}
              disabled={col.disabled ? col.disabled(rowData) : false}
              rounded
              outlined
              severity={col.severity}
              tooltip={col.tooltip}
              className="mr-2"
              onClick={() => col.onClick(rowData)}
            />
          ))}
        </div>
      </React.Fragment>
    );
  };

  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button style={{ backgroundColor: 'gray' }} label="Nie" icon="pi pi-times" outlined onClick={hideDeleteContactDialog} />
      <Button style={{ backgroundColor: '#e31818' }} label="Áno" icon="pi pi-check" severity="danger" onClick={deleteContact} />
    </React.Fragment>
  );

  // const onSelectAllChange = event => {
  //   const isChecked = event.checked;
  //
  //   if (isChecked) {
  //     // customerService.getCustomers().then(data => {
  //     //   setSelectAll(true);
  //     //   setSelectedContacts(data.customers);
  //     // });
  //   } else {
  //     setSelectAll(false);
  //     setSelectedContacts([]);
  //   }
  // };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between">
        {labelForCreateEntity && (
          <Button
            style={{ backgroundColor: '#e31818' }}
            type="button"
            icon="pi pi-plus"
            label={labelForCreateEntity}
            className="p-button-success"
            onClick={handleCreate}
          />
        )}
        {exportUrl && (
          <div>
            <Button
              type="button"
              style={{ backgroundColor: 'gray' }}
              icon="pi pi-download"
              label="Exportovať dáta"
              className="p-button-success"
              onClick={handleExport}
            />
          </div>
        )}
        {additionalHeaderButtons}
        {!labelForCreateEntity && <div />}
        {searchAllowed && (
          <div>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                id="search-id-table"
                value={filterValue || ''}
                onChange={e => {
                  if (e.target.value !== filterValue) {
                    setFilterValue(e.target.value);
                  }
                }}
                placeholder="Hľadať"
              />
            </span>
          </div>
        )}
      </div>
    );
  };

  const header = renderHeader();

  return (
    <div>
      <div className="card">
        {title && <h3>{title}</h3>}
        <DataTable
          stripedRows
          value={entities}
          size="small"
          lazy
          responsiveLayout="scroll"
          dataKey="id"
          emptyMessage="Žiadne dáta."
          header={header}
          paginator
          first={lazyParams.first}
          rows={20}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          filterDisplay="row"
          filters={filterValues}
          rowClassName={rowClassName}
          globalFilterFields={globalFilterFields}
          loading={loading || externalLoading}
          selection={onSelectionChange ? selectedItems : null}
          onSelectionChange={e => onSelectionChange(e.value)}
          onFilter={e => setFilterValues(e.filters)}
          // selectAll={selectAll}
          // onSelectAllChange={onSelectAllChange}
        >
          {/*(...columns)*/}
          {/*<Column field="number" header="Číslo" sortable></Column>*/}
          {/*<Column field="name" header="Meno" sortable></Column>*/}
          {/*<Column field="firstName" header="Prvé meno" sortable></Column>*/}
          {/*<Column field="lastName" header="Priezvisko" sortable></Column>*/}
          {/*<Column field="email" header="Email" sortable></Column>*/}
          {onSelectionChange && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>}

          {columns.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sortable}
              filter={col.filter}
              filterField={col.filterField ?? col.field}
              filterElement={col.filterElement}
              // filterMatchModeOptions={[ColumnFilterMatchModeOptions, FilterMatchMode.BETWEEN]}
              showFilterMenu={false}
              sortField={col.sort ? col.sort : col.field}
              body={col.formatter ? r => col.formatter(r[col.field], r) : undefined}
            />
          ))}
          {(editActionAllowed || createUrl || additionalActions) && (
            <Column body={actionBodyTemplate} header="Akcie" exportable={false} style={{ justifyContent: 'end' }}></Column>
          )}
        </DataTable>
      </div>
      <Dialog
        visible={deleteContactDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteProductDialogFooter}
        onHide={hideDeleteContactDialog}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          {entity && (
            <span>
              Naozaj chcete vymazať záznam: <b>{entity.id}</b>?
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default LazyTable;
